import {
  Amount,
  Avatar,
  Card,
  CardBody,
  Heading,
  Paragraph,
  Separator,
  Spinner,
  StructuredInfo,
  StructuredInfoItem,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { LoanCaseForOfferDTO } from "@smartx/mikro-vt-client-api"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { useAppDispatch, useOfferData } from "../../app/hooks"
import { setForcedTurnOff } from "../../app/slices/loaderSlice"
import { getOfferData } from "../../app/slices/offerDataSlice"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import ConfigContext from "../../providers/ConfigProvider"
import { RequestHeader } from "../../utils/utils"
import { OfferNavButtons, OfferSaveButton } from "./OfferNavButtons"

const DEFAULT_OFFER_EVALUATION_POOL_TIME_IN_SECS = 10

const SCREEN_STATUSES = [
  "EVALUATION_IN_PROGRESS",
  "EVALUATION_SUCCESS",
  "EVALUATION_MODIFICATION",
]

const getLoanAmount = (offerData: LoanCaseForOfferDTO) =>
  offerData?.productInstances?.find((el) => !!el)?.loanAmount || null

const OfferEvaluation = () => {
  const configContext = useContext(ConfigContext)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const intervalRef = useRef(null)
  const offerData = useOfferData()
  const evalState = offerData?.offerState
  const showButtons = [
    "EVALUATION_SUCCESS",
    "EVALUATION_MODIFICATION",
  ].includes(evalState)
  const isSuccess = evalState === "EVALUATION_SUCCESS"
  const [amount, setAmount] = useState(getLoanAmount(offerData))
  const { setNextStep } = useOfferScreenFlow()
  const offerEvaluationPoolTime =
    (configContext?.offerEvaluationPoolTime
      ? +configContext.offerEvaluationPoolTime
      : DEFAULT_OFFER_EVALUATION_POOL_TIME_IN_SECS) * 1000

  useEffect(() => {
    dispatch(setForcedTurnOff(evalState === "EVALUATION_IN_PROGRESS"))

    return () => {
      dispatch(setForcedTurnOff(false))
    }
  }, [dispatch, evalState])

  useEffect(() => {
    const loanAmount = getLoanAmount(offerData)
    let stopPolling = false

    switch (evalState) {
      case "EVALUATION_SUCCESS":
      case "EVALUATION_MODIFICATION": {
        setAmount(loanAmount)
        stopPolling = true
        break
      }

      case "EVALUATION_ERROR": {
        navigate("/offer/info/error", { replace: true })
        break
      }

      case "EVALUATION_FAILED": {
        navigate("/offer/info/failed", { replace: true })
        break
      }
    }

    intervalRef.current = setInterval(() => {
      if (evalState === "EVALUATION_IN_PROGRESS") {
        dispatch(
          getOfferData({
            header: new RequestHeader(),
            caseId: offerData?.caseId,
          }),
        )
      }

      if (stopPolling) {
        clearInterval(intervalRef.current)
        dispatch(setForcedTurnOff(false))
      }
    }, offerEvaluationPoolTime)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [
    dispatch,
    navigate,
    evalState,
    isSuccess,
    offerEvaluationPoolTime,
    offerData,
  ])

  const headerContent = (
    <Heading level={1}>
      {showButtons
        ? t("offers.evaluation.titleFinished")
        : t("offers.evaluation.title")}
    </Heading>
  )

  const spinnerContent = (
    <>
      {evalState === "EVALUATION_IN_PROGRESS" && (
        <Paragraph
          color={Card.COLOR.TRANSPARENT}
          style={{
            textAlign: "center",
            paddingTop: "5rem",
            paddingBottom: "5rem",
          }}
        >
          <Spinner size={Spinner.SIZE.LARGE} />
        </Paragraph>
      )}
    </>
  )

  const textContent = SCREEN_STATUSES?.includes(evalState) ? (
    <Paragraph style={{ marginBottom: "1rem" }}>
      {t("offers.evaluation.text." + evalState)}
    </Paragraph>
  ) : (
    ""
  )

  const amountCardContent = (
    <>
      {showButtons && (
        <Card color={Card.COLOR.FOREST}>
          <StructuredInfo
            alignment={StructuredInfo.ALIGNMENT.RIGHT}
            avatar={
              <Avatar
                icon={isSuccess ? Avatar.ICON.CHECKMARK : Avatar.ICON.WARNING}
                color={
                  isSuccess ? Avatar.COLOR.FOREST : Avatar.COLOR.WARNING_STRONG
                }
                backgroundColor={
                  isSuccess ? Avatar.COLOR.WHITE : Avatar.COLOR.WARNING_WEAK
                }
              />
            }
            header={
              <>
                <StructuredInfoItem size={StructuredInfoItem.SIZE.MEDIUM}>
                  {t("offers.evaluation.amount")}
                </StructuredInfoItem>
                <Amount
                  isAppendedCurrency
                  value={amount}
                  precision={0}
                  currency="Ft"
                  style={{
                    fontSize: "2rem",
                    color: "white",
                    marginTop: "2rem",
                  }}
                  thousandSeparator=" "
                />
              </>
            }
          />
        </Card>
      )}
    </>
  )

  const nextButtonHandler = () => {
    setNextStep(offerData?.caseId)
  }

  const navButtonsContent = (
    <>
      {showButtons && (
        <OfferNavButtons onNextButtonClicked={nextButtonHandler} />
      )}
    </>
  )

  const saveButtonHandler = () => {
    navigate("/dashboard", { replace: true })
  }

  const saveButtonContent = (
    <>
      {showButtons && (
        <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {headerContent}

        {textContent}

        {spinnerContent}

        {amountCardContent}

        {navButtonsContent}
      </>
    ),
    md: (
      <Card nested={false}>
        <CardBody>
          {headerContent}

          {textContent}

          {spinnerContent}

          {amountCardContent}
        </CardBody>

        {showButtons && (
          <>
            <Separator />
            <CardBody>{navButtonsContent}</CardBody>
          </>
        )}
      </Card>
    ),
  })

  if (!SCREEN_STATUSES.includes(evalState)) {
    return
  }

  return (
    <>
      {pageContent}

      {saveButtonContent}
    </>
  )
}

export default OfferEvaluation
